import React, { useState } from 'react';
import { Play, Download, Check, Loader2, Copy } from 'lucide-react';
import axios from 'axios';
import { usePlayer } from '../contexts/PlayerContext';

const API = process.env.REACT_APP_BACKEND_URL;

const sanitizeTitle = (title) => {
  return (title || 'Track').replace(/[\\/:*?"<>|\r\n\t]+/g, '').trim() || 'Track';
};

const PurchasedTrackCard = ({ track, tracks = [] }) => {
  const { playTrack, currentTrack, isPlaying } = usePlayer();
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);

  const isCurrentTrack = currentTrack?.id === track.id;

  const handlePlay = (e) => {
    e.stopPropagation();
    playTrack(track, tracks);
  };

  const handleCopyTitle = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(track.title || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      // Fallback: select the text node so the user can copy manually
      const range = document.createRange();
      const node = document.getElementById(`track-title-${track.id}`);
      if (node) {
        range.selectNodeContents(node);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }
  };

  const handleDownload = async (e) => {
    e.stopPropagation();
    if (downloading) return;
    setDownloading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${API}/api/music/tracks/${track.id}/download`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          responseType: 'blob',
        }
      );

      // Try to honor server-provided filename, otherwise build branded one
      let filename = `IAMHETHATIS - ${sanitizeTitle(track.title)}.mp3`;
      const cd = response.headers['content-disposition'] || response.headers['Content-Disposition'];
      if (cd) {
        const utf8Match = cd.match(/filename\*=UTF-8''([^;]+)/i);
        const asciiMatch = cd.match(/filename="?([^";]+)"?/i);
        if (utf8Match) {
          try { filename = decodeURIComponent(utf8Match[1]); } catch (_) { /* keep default */ }
        } else if (asciiMatch) {
          filename = asciiMatch[1];
        }
      }

      const blob = new Blob([response.data], {
        type: response.data.type || 'audio/mpeg',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Download failed';
      alert(`Download failed: ${msg}`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className={`bg-[#1A1A1A] border border-white/5 hover:border-[#D4AF37]/50 transition-all overflow-hidden group ${
        isCurrentTrack ? 'border-[#D4AF37]' : ''
      }`}
      data-testid={`purchased-track-card-${track.id}`}
    >
      <div className="relative aspect-square">
        {track.cover_art_url ? (
          <img src={track.cover_art_url} alt={track.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#0A2216] to-[#0D0D0D]" />
        )}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
          <button
            onClick={handlePlay}
            className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center text-black hover:scale-105 transition-transform"
            data-testid={`play-purchased-${track.id}`}
            aria-label={`Play ${track.title}`}
          >
            <Play size={20} className="ml-0.5" />
          </button>
        </div>
        <div className="absolute top-2 right-2 px-2 py-1 bg-green-600 text-xs uppercase tracking-wider flex items-center gap-1">
          <Check size={12} />
          Owned
        </div>
        {isCurrentTrack && isPlaying && (
          <div className="absolute bottom-2 right-2 flex gap-0.5">
            <div className="visualizer-bar" />
            <div className="visualizer-bar" />
            <div className="visualizer-bar" />
          </div>
        )}
      </div>

      <div className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <h3
            id={`track-title-${track.id}`}
            className="font-medium leading-snug select-text cursor-text break-words flex-1"
            title="Long-press or click to select. Use the copy button to copy."
            data-testid={`purchased-track-title-${track.id}`}
            style={{ userSelect: 'text', WebkitUserSelect: 'text' }}
          >
            {track.title}
          </h3>
          <button
            onClick={handleCopyTitle}
            className="flex-shrink-0 p-1.5 text-[#A3A3A3] hover:text-[#D4AF37] transition-colors"
            data-testid={`copy-title-${track.id}`}
            aria-label="Copy track title"
            title={copied ? 'Copied!' : 'Copy title'}
          >
            {copied ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
          </button>
        </div>

        {track.genre && (
          <span className="block text-xs text-[#A3A3A3] uppercase tracking-wider">{track.genre}</span>
        )}

        <button
          onClick={handleDownload}
          disabled={downloading}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-[#D4AF37] text-black text-sm font-medium uppercase tracking-wider hover:bg-[#E5C048] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid={`download-track-${track.id}`}
        >
          {downloading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Downloading...
            </>
          ) : (
            <>
              <Download size={16} />
              Download
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default PurchasedTrackCard;
